import {useState, useEffect} from 'react';
import Cookies from 'js-cookie';
import {useUserMe} from 'data/user';
import {useCurrentOrganization, getCurrentOrganizationIdCookie} from 'data/organization';
import {useApiKeys} from 'data/apiKey';
import {removeCrossSiteCookie} from 'utils/cookies';

export const useAuth = () => {
	const [apiToken, setApiToken] = useState(() => Cookies.get('api_token'));
	const [organizationId, setOrganizationId] = useState(() =>
		getCurrentOrganizationIdCookie(),
	);

	const {data: activeOrganization} = useCurrentOrganization({
		useQueryOptions: {
			enabled: !!apiToken && !!organizationId,
			refetchOnWindowFocus: false,
		},
	});

	const {data: user} = useUserMe({
		useQueryOptions: {
			enabled: !!organizationId && !!apiToken,
		},
	});

	const {data: apiKeys} = useApiKeys({
		useQueryOptions: {
			enabled: !!user && !!apiToken,
		},
	});

	// Cookie sync — detects external changes or token loss
	useEffect(() => {
		const syncAuthCookies = () => {
			const currentApiTokenCookie = Cookies.get('api_token');
			const currentOrgIdNumber = getCurrentOrganizationIdCookie();

			const tokenChanged = currentApiTokenCookie !== apiToken;
			const orgChanged = currentOrgIdNumber !== organizationId;

			if (!orgChanged && !tokenChanged) {
				return;
			}

			if (tokenChanged) {
				setApiToken(currentApiTokenCookie);
			}
			if (orgChanged) {
				setOrganizationId(currentOrgIdNumber);
			}

			// If there's no API token but organization_id exists, remove the organization_id
			// This prevents redirect loops when the user has organization_id but no valid token
			if (!currentApiTokenCookie && currentOrgIdNumber) {
				removeCrossSiteCookie('organization_id');
				setOrganizationId(null);
			}
		};

		// Run sync immediately on mount to handle edge cases
		syncAuthCookies();

		const interval = setInterval(syncAuthCookies, 3000);
		window.addEventListener('storage', syncAuthCookies);

		return () => {
			clearInterval(interval);
			window.removeEventListener('storage', syncAuthCookies);
		};
	}, [apiToken, organizationId]);

	return {
		apiToken,
		setApiToken,
		organizationId,
		setOrganizationId,
		activeOrganization,
		user,
		apiKeys,
	};
};
